import type {
  StaffRequestEvidenceApi,
  StaffRequestQuestionsApi,
  StaffRequestsApi,
} from "@lafayette/api-client";
import type { StaffRequestAttachmentUploadTarget } from "@lafayette/schemas";
import { createContext, type ReactNode, useContext } from "react";

// What the Requests pages need from outside: the typed API clients and the clock. The signed-in staff user's id comes from `GET /staff/me`
// (`useRequestViewer` in `access.ts`), not from the host.
// The admin app supplies the real ones (`admin-runtime.ts`); a test or the development harness supplies fakes.

export type RequestsRuntime = Readonly<{
  api: StaffRequestsApi;
  questions: StaffRequestQuestionsApi;
  /** The fulfilment evidence, customer updates and attachment metadata (CHT-752). STAFF ONLY data. */
  fulfilment: StaffRequestEvidenceApi;
  /**
   * CHT-754: how the bytes of an attachment reach the private object store: the browser's own request to the signed target. The file
   * goes straight to storage and never passes the Lafayette API.
   */
  uploadFile: (target: StaffRequestAttachmentUploadTarget, file: Blob) => Promise<void>;
  /** CHT-754: opens a signed download URL (it lives about a minute) in a new tab. The URL is never shown or logged. */
  openDownload: (url: string) => void;
  now: () => Date;
}>;

const RuntimeContext = createContext<RequestsRuntime | null>(null);

export function RequestsRuntimeProvider({
  value,
  children,
}: Readonly<{ value: RequestsRuntime; children: ReactNode }>) {
  return <RuntimeContext.Provider value={value}>{children}</RuntimeContext.Provider>;
}

export function useRequestsRuntime(): RequestsRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error("The request pages need a RequestsRuntimeProvider.");
  return runtime;
}

/** The browser's PUT to the signed target of one attachment: the URL, the method and the headers that the API made. */
export async function putAttachmentToTarget(
  target: StaffRequestAttachmentUploadTarget,
  file: Blob,
  fetcher: typeof fetch = fetch,
): Promise<void> {
  // Never put the URL in the message: it is a signed credential.
  const response = await fetcher(target.url, {
    method: target.method,
    headers: target.headers,
    body: file,
  });
  if (!response.ok) throw new Error(`storage answered ${response.status}`);
}

/** Opens a signed download URL in a new tab, with no opener and no referrer. */
export function openDownloadInNewTab(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}
